import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Gift, Heart, Pen } from 'lucide-react';

export default function ArtikelPersonalisierteGeschenke() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />Alle Ratgeber
          </Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4">
            <Calendar className="w-4 h-4" /><span>Februar 2026</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">
            Personalisierte Eid Geschenkideen: So machst du es richtig
          </h1>
          <p className="text-lg text-white/80 leading-relaxed">
            Individuell gravierte, bestickte oder bedruckte Geschenke – so wird dein Eid-Geschenk einzigartig und unvergesslich.
          </p>
        </div>
      </section>

      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 prose prose-lg max-w-none">
          <p className="text-xl text-[#2C332E] leading-relaxed mb-8">
            Ein personalisiertes Geschenk zeigt besondere Wertschätzung – es sagt: "Ich habe mir Gedanken gemacht, 
            speziell für dich." Zu Eid sind individuell gestaltete Geschenke besonders beliebt, weil sie die 
            Verbundenheit mit Familie und Freunden ausdrücken.
          </p>

          <div className="bg-[#F5F5F0] rounded-2xl p-6 md:p-8 mb-12">
            <h2 className="font-serif text-xl font-medium text-[#2C332E] mb-4">📖 Inhaltsverzeichnis</h2>
            <ul className="space-y-2 text-[#4A5D4F]">
              <li><a href="#gravur" className="hover:underline">1. Gravierte Geschenke</a></li>
              <li><a href="#bestickt" className="hover:underline">2. Bestickte Geschenke</a></li>
              <li><a href="#bedruckt" className="hover:underline">3. Bedruckte Geschenke</a></li>
              <li><a href="#handgemacht" className="hover:underline">4. Handgeschriebenes & Handgemachtes</a></li>
              <li><a href="#tipps" className="hover:underline">5. Tipps für die Personalisierung</a></li>
            </ul>
          </div>

          <section id="gravur" className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                <Pen className="w-6 h-6 text-[#4A5D4F]" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">1. Gravierte Geschenke</h2>
            </div>
            
            <p className="text-[#4B5563] leading-relaxed mb-6">
              Gravuren verleihen Gegenständen eine persönliche Note, die ein Leben lang hält. 
              Besonders beliebt sind arabische Namen oder kurze Duas.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Tasbih mit Namensgravur</h4>
                <p className="text-sm text-[#4B5563]">Eine Gebetskette mit dem Namen des Beschenkten in arabischer oder deutscher Schrift. Zeitlos und spirituell bedeutsam.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Schmuck mit Gravur</h4>
                <p className="text-sm text-[#4B5563]">Armband, Kette oder Ring mit "Bismillah", dem Namen oder einem Dua. Besonders schön für Frauen.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Quran-Ständer mit Gravur</h4>
                <p className="text-sm text-[#4B5563]">Ein hochwertiger Rehal (Quran-Ständer) aus Holz mit eingravierten Initialen oder einem Vers.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Parfümflakon mit Gravur</h4>
                <p className="text-sm text-[#4B5563]">Elegante Attar-Flakons mit dem Namen des Beschenkten – luxuriös und einzigartig.</p>
              </div>
            </div>
          </section>

          <section id="bestickt" className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                <Heart className="w-6 h-6 text-[#4A5D4F]" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">2. Bestickte Geschenke</h2>
            </div>

            <p className="text-[#4B5563] leading-relaxed mb-6">
              Stickereien haben eine lange Tradition in der islamischen Kultur. Handbestickte oder maschinell bestickte Geschenke sind besonders hochwertig.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Gebetsteppich mit Namen</h4>
                <p className="text-sm text-[#4B5563]">Ein personalisierter Gebetsteppich mit eingesticktem Namen – das beliebteste personalisierte Eid-Geschenk überhaupt!</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Hijab oder Abaya mit Monogramm</h4>
                <p className="text-sm text-[#4B5563]">Dezent bestickte Initialen am Saum – elegant und persönlich.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Kufi/Taqiyah mit Namen</h4>
                <p className="text-sm text-[#4B5563]">Traditionelle Kopfbedeckung für Männer mit eingesticktem Namen oder Muster.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Quran-Hülle mit Stickerei</h4>
                <p className="text-sm text-[#4B5563]">Eine Samt- oder Stoffhülle für den Quran mit Namen und islamischen Mustern.</p>
              </div>
            </div>
          </section>

          <section id="bedruckt" className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                <Gift className="w-6 h-6 text-[#4A5D4F]" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">3. Bedruckte Geschenke</h2>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Personalisierte Tasse</h4>
                <p className="text-sm text-[#4B5563]">"Eid Mubarak, [Name]" oder ein schöner Vers – perfekt für den Morgentee.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Fotobuch "Unsere Eid-Erinnerungen"</h4>
                <p className="text-sm text-[#4B5563]">Ein Fotobuch mit Bildern vergangener Eid-Feste – emotional und einzigartig.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Kunstdruck mit Namen</h4>
                <p className="text-sm text-[#4B5563]">Arabische Kalligraphie mit dem Namen oder einem Dua als Wandbild.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Personalisierte Geschenkbox</h4>
                <p className="text-sm text-[#4B5563]">Holzbox mit Lasergravur oder bedrucktem Deckel – fülle sie mit Datteln oder Süßigkeiten.</p>
              </div>
            </div>
          </section>

          <section id="handgemacht" className="mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-6">4. Handgeschriebenes & Handgemachtes</h2>
            
            <div className="space-y-4 mb-6">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Handgeschriebener Brief</h4>
                <p className="text-sm text-[#4B5563]">Ein persönlicher Brief, der ausdrückt, warum diese Person wichtig ist. Kostenlos, aber unbezahlbar.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Selbstgemachte Kalligraphie</h4>
                <p className="text-sm text-[#4B5563]">Ein Vers oder Dua, von Hand geschrieben und gerahmt.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Gutschein-Buch</h4>
                <p className="text-sm text-[#4B5563]">"Gutschein für: 1x gemeinsames Kochen, 1x Ausflug, 1x Massage" – persönliche Zeit verschenken.</p>
              </div>
            </div>
          </section>

          <section id="tipps" className="mb-12">
            <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-6">5. Tipps für die Personalisierung</h2>
            
            <div className="bg-[#F5F5F0] rounded-xl p-6">
              <ul className="space-y-3 text-[#4B5563]">
                <li><strong>Früh bestellen:</strong> Personalisierte Produkte brauchen oft 1-2 Wochen Vorlauf.</li>
                <li><strong>Namen korrekt schreiben:</strong> Besonders bei arabischer Schrift auf korrekte Schreibweise achten.</li>
                <li><strong>Qualität prüfen:</strong> Bei Online-Bestellungen Bewertungen lesen und ggf. vorher ein Proof anfordern.</li>
                <li><strong>Verpackung nicht vergessen:</strong> Ein personalisiertes Geschenk verdient eine schöne Präsentation.</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Fazit</h2>
            <p className="text-[#4B5563] leading-relaxed mb-4">
              Ein personalisiertes Geschenk zeigt, dass Sie sich Zeit genommen haben. Es wird nicht in einer 
              Schublade verschwinden, sondern täglich genutzt und geschätzt. Ob Tasbih mit Gravur, 
              Gebetsteppich mit Namen oder ein handgeschriebener Brief – die persönliche Note macht den Unterschied.
            </p>
            <p className="text-xl text-[#4A5D4F] font-medium">Eid Mubarak! 🌙</p>
          </section>

          <div className="bg-[#F5F5F0] rounded-2xl p-6 mb-8">
            <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-4">Weitere Artikel</h3>
            <ul className="space-y-2">
              <li><Link to="/ratgeber/diy-eid-geschenke" className="text-[#4A5D4F] hover:underline">→ DIY Eid Geschenke selber machen</Link></li>
              <li><Link to="/ratgeber/beste-eid-geschenke-2026" className="text-[#4A5D4F] hover:underline">→ Die besten Eid Geschenke 2026</Link></li>
            </ul>
          </div>

          <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
            <h3 className="font-serif text-2xl font-medium text-white mb-4">Geschenke entdecken</h3>
            <Link to="/geschenke" className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors">
              Jetzt stöbern<Gift className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
}
